import { useState } from 'react'
import { ConnectionAmpsField } from '../components/ConnectionAmpsField'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  useAddProjectComment,
  useApplyTemplate,
  useCreateTask,
  usePriorities,
  useProject,
  useProjectStatuses,
  useProjectTemplates,
  useUpdateProject,
  useUsers
} from '../api/queries'
import { useAuth } from '../auth/AuthProvider'
import { ArchiveProjectButton } from '../components/ArchiveProjectButton'
import { DeleteProjectCard } from '../components/DeleteProjectCard'
import { ProjectDevicesCard } from '../components/ProjectDevicesCard'
import { ProjectKindControl, ServiceDevicesCard } from '../components/ServiceDevicesCard'
import { RelatedManualsCard } from '../components/RelatedManualsCard'
import { SiteContactFields } from '../components/SiteContact'
import { WorkSiteField } from '../components/WorkSite'
import { CaseNumberControl } from '../components/CaseNumberControl'
import { OrderNumberControl } from '../components/OrderNumberControl'
import { ProjectManagerControl } from '../components/ProjectManagerControl'
import { CustomerControl } from '../components/CustomerControl'
import { workSiteLine } from '../lib/projectLabel'
import { progressColor, progressTextColor } from '../lib/progressColor'
import { ProjectFilesCard } from '../components/ProjectFilesCard'
import { ProjectMaterialsCard } from '../components/ProjectMaterialsCard'
import { ReplacedPartsCard } from '../components/ReplacedPartsCard'
import { HandoverCard } from '../components/HandoverCard'
import { ProjectActivityLogButton } from '../components/ProjectActivityLog'
import { DefectsCard } from '../components/DefectsCard'
import { ProjectNeedsCard } from '../components/ProjectNeedsCard'
import { CaseBoxCard } from '../components/CaseBoxCard'
import { SelfChecksCard, useSelfCheckWarning } from '../components/SelfChecks'
import { ProjectDiagramsCard } from '../components/ProjectDiagramsCard'
import { TaskItem } from '../components/TaskItem'
import type { UserRef } from '../api/types'
import { Avatar, Card, EmptyState, ErrorBox, Loading, PriorityPill, ProgressBar, StatusPill } from '../components/ui'
import { formatDate, formatDateTime, deadlineLabel } from '../lib/format'

/** Projektdetaljer med opgaver, næste handling, status og kommentarer. */
export function ProjectDetailPage() {
  const { id } = useParams()
  const projectId = Number(id)
  const { data, isLoading, error } = useProject(projectId)
  const { isProjectManager, isSuperadmin } = useAuth()
  // Kommer man fra QR-koden på sagskassen (/s/<id>), står ?kasse=1 på - så fremhæves kassen (beslutning 144).
  const [searchParams] = useSearchParams()
  // ?kasse=<nr>: hvilken af sagens kasser, der blev scannet (beslutning 145).
  const scannedBox = Number(searchParams.get('kasse')) || null

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <EmptyState>Projektet blev ikke fundet.</EmptyState>

  const { project } = data

  // Serveren håndhæver den endelige regel; her styrer vi kun om knapperne vises.
  // Samme regel som serverens CanEditProject: Superadmin, Administrator og Projektleder
  // må rette ALLE sager (beslutning 066). Før krævede siden også, at man var sagens
  // egen projektleder, så knapperne manglede for alle andre (beslutning 084).
  const canEdit = isProjectManager
  const site = workSiteLine(project)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card>
        <div className="row row--between" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 22 }}>{project.name}</h2>
            <div className="muted small">
              {/* Kunden kan skiftes, hvis sagen er oprettet på den forkerte (beslutning 084). */}
              <CustomerControl project={project} canEdit={canEdit} />
              {project.teamName ? ` · ${project.teamName}` : ''}
            </div>
            {/* Sagens egen adresse med fed, som på sagskortet (beslutning 077). */}
            {site && (
              <div className="small" style={{ fontWeight: 700 }}>
                {site}
              </div>
            )}
            <OrderNumberControl project={project} canEdit={canEdit} />
            {/* Kundens eget nummer ved siden af vores (beslutning 083). */}
            <CaseNumberControl project={project} canEdit={canEdit} />
          </div>
          <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Koder til porte og nøglebokse - alle på sagen skal kunne komme ind (beslutning 088). */}
            <Link className="btn btn--sm" to={`/projects/${projectId}/access-codes`}>
              🔑 Port og nøgleboks
            </Link>
            {/* Mærkat med QR til sagskassen (beslutning 143). */}
            <Link className="btn btn--sm" to={`/projects/${projectId}/label`}>
              🏷️ Mærkater til sagskasser
            </Link>
            {/* Hvad der er sket på sagen, og hvem der lukkede hvad (beslutning 086). */}
            {isProjectManager && <ProjectActivityLogButton projectId={projectId} projectName={project.name} />}
            {/* Arkivér eller gendan (beslutning 090). */}
            {canEdit && <ArchiveProjectButton project={project} />}
            <ProjectKindControl project={project} canEdit={canEdit} />
            <StatusPill status={project.status} />
            <PriorityPill priority={project.priority} />
          </div>
        </div>

        {project.isArchived && (
          <div className="error-box" style={{ marginTop: 12, background: '#F3F4F6', borderColor: '#D1D5DB', color: '#374151' }}>
            <strong>Sagen er arkiveret</strong>
            {data.archivedAt && <span> {formatDate(data.archivedAt)}</span>}. Den står ikke i sagslisten, men kan
            findes under "Vis arkiv".
          </div>
        )}

        {data.description && <p className="small" style={{ marginTop: 12 }}>{data.description}</p>}

        <div style={{ marginTop: 14 }}>
          <div className="row row--between tiny muted" style={{ marginBottom: 4 }}>
            <span>
              {/* En tom sag stod før med "0 af 0 opgaver" og en tom bjælke. Det
                  ligner en fejl frem for en forklaring, så her siges det ligeud. */}
              {project.taskCount === 0 && !project.progressIsManual
                ? 'Fremdrift beregnes ud fra opgaverne - sagen har ingen endnu'
                : `Fremdrift ${project.completedTaskCount} af ${project.taskCount} opgaver` +
                  (project.progressIsManual ? ' (manuelt sat)' : ' (beregnet automatisk)')}
            </span>
            {project.taskCount === 0 && !project.progressIsManual ? (
              <span>-</span>
            ) : (
              <span style={{ color: progressTextColor(project.progressPercent), fontWeight: 700 }}>
                {project.progressPercent}%
              </span>
            )}
          </div>
          {/* Rød/gul/grøn som på TV-skærmen (Simon 2026-10-28). */}
          <ProgressBar value={project.progressPercent} color={progressColor(project.progressPercent)} />
          {project.taskCount === 0 && !project.progressIsManual && canEdit && (
            <p className="tiny muted" style={{ marginTop: 6 }}>
              Tilføj opgaver nedenfor - enten én ad gangen eller fra en procedureskabelon. Så
              flytter fremdriften sig af sig selv, efterhånden som de bliver færdige.
            </p>
          )}
        </div>

        <div className="form-grid" style={{ marginTop: 16 }}>
          {/* Telefon og adresse står her, så man kan ringe og finde vej uden at
              klikke videre til kundekortet - man sidder typisk i bilen. */}
          <SiteContactFields contact={data.site} />
          <WorkSiteField site={data.workSite} />
          {/* Rettigheder i ampere der, hvor arbejdet foregår (beslutning 109). */}
          <ConnectionAmpsField
            projectId={project.id}
            amps={data.connectionAmps}
            onSite={data.connectionAmpsOnSite}
            canEdit={canEdit}
          />
          {/* Kan skiftes af Superadmin, Administrator og Projektleder (beslutning 084). */}
          <ProjectManagerControl project={project} canEdit={canEdit} />
          <div>
            <div className="tiny muted">Startdato</div>
            <div className="small">{formatDate(project.startDate)}</div>
          </div>
          <div>
            <div className="tiny muted">Deadline</div>
            <div className="small">
              {formatDate(project.deadline)}
              {project.deadline && <span className="muted"> · {deadlineLabel(project.deadline)}</span>}
            </div>
          </div>
          <div>
            <div className="tiny muted">Senest ændret</div>
            <div className="small">{formatDateTime(project.updatedAt)}</div>
          </div>
        </div>

        {project.status.isWaiting && (
          <div className="error-box" style={{ marginTop: 14, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
            <strong>Afventer:</strong> {project.waitingReason ?? 'Ingen årsag angivet'}
            {project.waitingSince && <span className="tiny"> (siden {formatDate(project.waitingSince)})</span>}
          </div>
        )}
      </Card>

      {/* Hvad der mangler til næste gang - øverst, så det ses før man kører (beslutning 143). */}
      {/* Hvor sagskassen er - i en montørs bil eller på firmaet (beslutning 144). */}
      <CaseBoxCard projectId={projectId} highlight={scannedBox} />

      <ProjectNeedsCard projectId={projectId} />

      <NextActionCard project={project} canEdit={canEdit} />


      {/* Manualerne til den slags sag, den her er oprettet fra (beslutning
          063). Skabelon-id-et er KUN en reference tilbage - opgaverne er
          kopieret ind (beslutning 021). */}
      <RelatedManualsCard
        templateId={data.createdFromTemplateId}
        title="Manualer til denne slags sag"
      />

      {/* En servicesag handler om enheder, der allerede står hos kunden (beslutning
          076); en almindelig sag registrerer de enheder, den installerer. */}
      {project.kind === 'service' ? (
        <>
          <ServiceDevicesCard project={project} canEdit={canEdit} />
          {/* Det, montoeren har skiftet, med stregkode og lager (beslutning 098). */}
          <ReplacedPartsCard project={project} />
        </>
      ) : (
        <ProjectDevicesCard project={project} canEdit={canEdit} />
      )}

      <ProjectFilesCard projectId={projectId} />

      {/* Hvad der gik til sagen (beslutning 071). Staar foer mangellisten, fordi
          materialerne er en del af det udfoerte arbejde - manglerne er det, der
          staar tilbage. */}
      <ProjectMaterialsCard projectId={projectId} />

      <DefectsCard projectId={projectId} />

      {/* Egenkontrol (beslutning 131) - stoette til husets KLS. Kun almindelige sager. */}
      <SelfChecksCard projectId={projectId} />

      {/* Diagrammer (beslutning 150) - kun Projektleder og opefter. */}
      {isProjectManager && <ProjectDiagramsCard projectId={projectId} />}

      <HandoverCard projectId={projectId} projectName={project.name} />

      <Card
        title={`Opgaver (${data.tasks.length})`}
        actions={
          canEdit ? (
            <div className="row">
              <ApplyTemplateButton projectId={projectId} />
              <NewTaskButton projectId={projectId} />
            </div>
          ) : undefined
        }
      >
        {data.tasks.length === 0 ? (
          <EmptyState>Der er ingen opgaver på projektet endnu.</EmptyState>
        ) : (
          <TaskSections tasks={data.tasks} />
        )}
      </Card>

      <MembersCard projectId={projectId} members={data.members} canEdit={canEdit} />

      <CommentsCard projectId={projectId} comments={data.comments} />

      {isSuperadmin && <DeleteProjectCard project={project} />}
    </div>
  )
}

/**
 * Opgaverne under deres hovedemner fra skabelonen (beslutning 141), i den rækkefølge
 * hovedemnerne først optræder. Hvert hovedemne viser sin andel af sagen (summen af
 * underemnernes vægt) og hvor langt det er. Opgaver uden hovedemne står til sidst under
 * "Øvrige opgaver" - eller uden overskrift, hvis sagen slet ikke har hovedemner.
 */
function TaskSections({ tasks }: { tasks: import('../api/types').Task[] }) {
  const groups: { section: string | null; tasks: import('../api/types').Task[] }[] = []
  for (const task of tasks) {
    const section = task.section?.trim() || null
    const group = groups.find((g) => g.section === section)
    if (group) group.tasks.push(task)
    else groups.push({ section, tasks: [task] })
  }
  // Opgaver uden hovedemne til sidst.
  groups.sort((a, b) => Number(a.section === null) - Number(b.section === null))

  if (groups.length === 1 && groups[0].section === null) {
    return (
      <div className="stack">
        {tasks.map((task) => (
          <TaskItem key={task.id} task={task} showProject={false} />
        ))}
      </div>
    )
  }

  const totalWeight = tasks
    .filter((t) => !t.status.isCancelled)
    .reduce((sum, t) => sum + Math.max(t.weight, 1), 0)

  return (
    <div className="stack" style={{ gap: 18 }}>
      {groups.map((group) => {
        const active = group.tasks.filter((t) => !t.status.isCancelled)
        const weight = active.reduce((sum, t) => sum + Math.max(t.weight, 1), 0)
        const done = active.reduce(
          (sum, t) => sum + Math.max(t.weight, 1) * (t.status.isCompleted ? 100 : Math.min(Math.max(t.progressPercent, 0), 100)),
          0
        )
        const progress = weight === 0 ? 0 : Math.round(done / weight)
        const share = totalWeight === 0 ? 0 : Math.round((weight / totalWeight) * 100)

        return (
          <section key={group.section ?? '-'}>
            <div className="row row--between" style={{ marginBottom: 6, gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>
                {group.section ?? 'Øvrige opgaver'}
                <span className="small muted" style={{ fontWeight: 400 }}> · {share} % af sagen</span>
              </h3>
              <span className="small" style={{ color: progressTextColor(progress), fontWeight: 700 }}>
                {progress}% færdig
              </span>
            </div>
            <ProgressBar value={progress} color={progressColor(progress)} />
            <div className="stack" style={{ marginTop: 8 }}>
              {group.tasks.map((task) => (
                <TaskItem key={task.id} task={task} showProject={false} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function NextActionCard({ project, canEdit }: { project: import('../api/types').Project; canEdit: boolean }) {
  const update = useUpdateProject(project.id)
  const { data: users } = useUsers()
  const { data: statuses } = useProjectStatuses()
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(project.nextActionText ?? '')
  const [userId, setUserId] = useState(project.nextActionUser?.id ? String(project.nextActionUser.id) : '')
  const [deadline, setDeadline] = useState(project.nextActionDeadline?.slice(0, 10) ?? '')
  const [statusId, setStatusId] = useState(String(project.status.id))
  const [waitingReason, setWaitingReason] = useState(project.waitingReason ?? '')
  const [error, setError] = useState<string | null>(null)

  const selectedStatus = statuses?.find((s) => String(s.id) === statusId)
  const selfCheckWarning = useSelfCheckWarning(project.id)

  async function save() {
    setError(null)
    // Egenkontrol (beslutning 131): advarer - spaerrer ikke - naar en sag afsluttes uden.
    if (selectedStatus?.isCompleted && String(project.status.id) !== statusId && selfCheckWarning) {
      if (!window.confirm(selfCheckWarning)) return
    }
    try {
      await update.mutateAsync({
        nextActionText: text,
        nextActionUserId: userId ? Number(userId) : 0,
        nextActionDeadline: deadline ? new Date(deadline).toISOString() : null,
        statusId: Number(statusId),
        waitingReason: waitingReason || null
      })
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme.')
    }
  }

  return (
    <Card
      title="Næste handling"
      actions={
        canEdit && !editing ? (
          <button className="btn btn--sm" onClick={() => setEditing(true)}>
            Rediger
          </button>
        ) : undefined
      }
    >
      {!editing ? (
        <>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{project.nextActionText || 'Ingen næste handling angivet'}</div>
          <div className="small muted" style={{ marginTop: 4 }}>
            Ansvarlig: {project.nextActionUser?.displayName ?? '-'}
            {project.nextActionDeadline ? ` · Deadline ${formatDate(project.nextActionDeadline)}` : ''}
          </div>
        </>
      ) : (
        <>
          <label className="field">
            <span className="field__label">Hvad skal der ske som det næste?</span>
            <input value={text} onChange={(e) => setText(e.target.value)} />
          </label>

          <div className="form-grid">
            <label className="field">
              <span className="field__label">Ansvarlig</span>
              <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Ingen</option>
                {users?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Deadline</span>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </label>

            <label className="field">
              <span className="field__label">Projektstatus</span>
              <select value={statusId} onChange={(e) => setStatusId(e.target.value)}>
                {statuses?.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {selectedStatus?.isWaiting && (
            <label className="field">
              <span className="field__label">Afventer årsag *</span>
              <input value={waitingReason} onChange={(e) => setWaitingReason(e.target.value)} />
            </label>
          )}

          {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

          <div className="btn-group">
            <button className="btn btn--primary btn--sm" onClick={() => void save()} disabled={update.isPending}>
              Gem
            </button>
            <button className="btn btn--sm" onClick={() => setEditing(false)}>
              Fortryd
            </button>
          </div>
        </>
      )}
    </Card>
  )
}

/**
 * Medarbejdere på sagen.
 *
 * Medlemskabet er ikke bare pynt: det afgør, hvem der kan se sagen på sin egen
 * liste. Projektlederen og alle med en opgave på sagen kan se den i forvejen -
 * medlemmerne er dem, der skal med, uden at have en opgave endnu.
 */
function MembersCard({
  projectId,
  members,
  canEdit
}: {
  projectId: number
  members: UserRef[]
  canEdit: boolean
}) {
  const { data: users } = useUsers()
  const update = useUpdateProject(projectId)

  const [editing, setEditing] = useState(false)
  const [chosen, setChosen] = useState<number[]>([])
  const [error, setError] = useState<string | null>(null)

  function start() {
    setChosen(members.map((m) => m.id))
    setError(null)
    setEditing(true)
  }

  async function save() {
    setError(null)
    try {
      await update.mutateAsync({ memberIds: chosen })
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme medlemmerne.')
    }
  }

  return (
    <Card
      title="Medarbejdere på sagen"
      actions={
        canEdit && !editing ? (
          <button className="btn btn--sm" onClick={start}>
            Rediger
          </button>
        ) : undefined
      }
    >
      {editing ? (
        <>
          <div className="cal-people">
            {users?.map((u) => (
              <label key={u.id} className={`cal-person ${chosen.includes(u.id) ? 'cal-person--on' : ''}`}>
                <input
                  type="checkbox"
                  checked={chosen.includes(u.id)}
                  onChange={() =>
                    setChosen((current) =>
                      current.includes(u.id) ? current.filter((x) => x !== u.id) : [...current, u.id]
                    )
                  }
                />
                <Avatar user={u} size={22} />
                <span>{u.displayName}</span>
              </label>
            ))}
          </div>

          {error && <div className="error-box" style={{ marginTop: 10 }}>{error}</div>}

          <div className="btn-group" style={{ marginTop: 12 }}>
            <button className="btn btn--primary btn--sm" disabled={update.isPending} onClick={() => void save()}>
              {update.isPending ? 'Gemmer ...' : 'Gem'}
            </button>
            <button className="btn btn--sm" onClick={() => setEditing(false)}>
              Fortryd
            </button>
          </div>
        </>
      ) : members.length === 0 ? (
        <div className="muted small">
          Ingen medarbejdere tilknyttet.
          {canEdit && ' Tryk Rediger for at sætte dem på - så kan de se sagen på deres egen liste.'}
        </div>
      ) : (
        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {members.map((member) => (
            <span key={member.id} className="pill" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
              {member.displayName}
            </span>
          ))}
        </div>
      )}
    </Card>
  )
}

/** Tilføjer en procedures opgaver til en sag der allerede er oprettet. */
function ApplyTemplateButton({ projectId }: { projectId: number }) {
  const { data: templates } = useProjectTemplates()
  const apply = useApplyTemplate(projectId)
  const [templateId, setTemplateId] = useState('')

  if (!templates || templates.length === 0) {
    return null
  }

  return (
    <div className="row">
      <select
        style={{ maxWidth: 190 }}
        value={templateId}
        onChange={(event) => setTemplateId(event.target.value)}
      >
        <option value="">Tilføj fra procedure ...</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.procedureName ? `${template.procedureName}: ` : ''}
            {template.name} ({template.taskCount})
          </option>
        ))}
      </select>
      <button
        className="btn btn--sm"
        disabled={!templateId || apply.isPending}
        onClick={async () => {
          await apply.mutateAsync(Number(templateId))
          setTemplateId('')
        }}
      >
        {apply.isPending ? 'Tilføjer ...' : 'Tilføj'}
      </button>
    </div>
  )
}

function NewTaskButton({ projectId }: { projectId: number }) {
  const [open, setOpen] = useState(false)
  const createTask = useCreateTask()
  const { data: users } = useUsers()
  const { data: priorities } = usePriorities()
  const [title, setTitle] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [deadline, setDeadline] = useState('')
  const [priorityId, setPriorityId] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await createTask.mutateAsync({
        projectId,
        title,
        assigneeId: assigneeId ? Number(assigneeId) : null,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        priorityId: priorityId ? Number(priorityId) : null
      })
      setTitle('')
      setAssigneeId('')
      setDeadline('')
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke oprette opgaven.')
    }
  }

  if (!open) {
    return (
      <button className="btn btn--sm btn--primary" onClick={() => setOpen(true)}>
        Ny opgave
      </button>
    )
  }

  return (
    <form onSubmit={submit} style={{ width: '100%' }}>
      <div className="form-grid">
        <label className="field">
          <span className="field__label">Titel *</span>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Ansvarlig</span>
          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">Ingen</option>
            {users?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Deadline</span>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Prioritet</span>
          <select value={priorityId} onChange={(e) => setPriorityId(e.target.value)}>
            <option value="">Normal</option>
            {priorities?.map((priority) => (
              <option key={priority.id} value={priority.id}>
                {priority.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

      <div className="btn-group">
        <button className="btn btn--sm btn--primary" type="submit" disabled={createTask.isPending}>
          Opret opgave
        </button>
        <button className="btn btn--sm" type="button" onClick={() => setOpen(false)}>
          Fortryd
        </button>
      </div>
    </form>
  )
}

function CommentsCard({ projectId, comments }: { projectId: number; comments: import('../api/types').Comment[] }) {
  const addComment = useAddProjectComment(projectId)
  const [body, setBody] = useState('')

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!body.trim()) return
    await addComment.mutateAsync(body.trim())
    setBody('')
  }

  return (
    <Card title={`Kommentarer (${comments.length})`}>
      <div className="stack" style={{ marginBottom: 14 }}>
        {comments.length === 0 && <div className="muted small">Ingen kommentarer endnu.</div>}
        {comments.map((comment) => (
          <div key={comment.id} style={{ borderLeft: '3px solid var(--border)', paddingLeft: 10 }}>
            <div className="tiny muted">
              {comment.user.displayName} · {formatDateTime(comment.createdAt)}
            </div>
            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
              {comment.body}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit}>
        <textarea
          placeholder="Skriv en kommentar. Brug @navn for at nævne en kollega."
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <button className="btn btn--sm btn--primary" type="submit" disabled={!body.trim() || addComment.isPending}>
          Send
        </button>
      </form>
    </Card>
  )
}
